import { parseChord } from './chord';
import { resolveCollisions, signature, type Candidate } from './collisionPass';
import { keybindingCommandIds } from './defaultKeybindings';
import { applyReservedChordPass, findWarnings, isAddedByMutation } from './keymapWarnings';
import { resolveKeymap } from './resolveKeymap';
import { chordsLostToSavedOverrides } from './savedOverrideLosses';
import { jamScheme } from './schemes/jam';
import type {
  KeybindingCommandId,
  KeybindingOverrides,
  KeybindingScheme,
  KeyboardLayoutSnapshot,
  Keymap,
  KeymapValidationResult,
  Platform,
} from './keybindingTypes';

export type ValidateKeymapOptions = {
  readonly scheme: KeybindingScheme;
  readonly overrides: KeybindingOverrides;
  readonly platform: Platform;
  readonly layoutMap: KeyboardLayoutSnapshot;
  readonly isDev?: boolean;
  readonly mutatedCommandId?: KeybindingCommandId;
  /** The chords the mutation added to `mutatedCommandId`; only these can be refused durably as reserved. */
  readonly mutatedChords?: readonly string[];
  /**
   * While the keyboard layout is still loading, physical aliases are unknown,
   * so no durable repair is made except for chords the mutation added (I1).
   * Defaults to `layoutMap.status === 'loading'`.
   */
  readonly deferRepairs?: boolean;
  /** The keymap before the mutation: a default chord that was already inactive is never written as a loser (O1 ii). */
  readonly previousKeymap?: Keymap;
};

/**
 * Collision precedence of one chord (D6): a chord the current mutation added
 * wins; every other chord — the rest of the mutated row included — keeps its
 * stored precedence (user override > scheme > Jam default), then registry and
 * chord order. The first edit of a row that still uses its defaults turns its
 * chords into saved shortcuts (override precedence), leaving out every chord
 * that was inactive because it lost to another command's saved override —
 * `default-shadowed`, a layout-dependent overlap, or a platform-only conflict
 * (N1, O1). Reserved chords and same-row duplicates are kept.
 */
function sourcePriority(options: ValidateKeymapOptions, commandId: KeybindingCommandId, chord: string): number {
  if (isAddedByMutation(commandId, chord, options.mutatedCommandId, options.mutatedChords)) return 4;
  if (Array.isArray(options.overrides[commandId])) return 3;
  const current = options.scheme.defaults[commandId][options.platform];
  const jam = jamScheme.defaults[commandId][options.platform];
  return current.join('\0') === jam.join('\0') ? 1 : 2;
}

export { findWarnings } from './keymapWarnings';

export function validateKeymap(options: ValidateKeymapOptions): KeymapValidationResult {
  const isDev = options.isDev ?? false;
  const previousKeymap = options.previousKeymap;
  const initial = resolveKeymap(options.scheme, options.overrides, options.platform);
  const { durable, working, notices, durableChanges } = applyReservedChordPass(
    initial,
    options.platform,
    options.layoutMap,
    isDev,
    options.mutatedCommandId,
    options.mutatedChords,
  );

  const candidates: Candidate[] = [];
  keybindingCommandIds.forEach((commandId, registryIndex) => working[commandId].forEach((chord, chordIndex) => {
    const parsed = parseChord(chord);
    const signed = parsed ? signature(parsed, options.platform, options.layoutMap) : null;
    if (signed) {
      candidates.push({
        commandId, chord, signature: signed, priority: sourcePriority(options, commandId, chord),
        stored: Array.isArray(options.overrides[commandId]), registryIndex, chordIndex,
      });
    }
  }));
  candidates.sort((a, b) => b.priority - a.priority || a.registryIndex - b.registryIndex || a.chordIndex - b.chordIndex);

  const accepted = resolveCollisions(candidates, {
    mutatedCommandId: options.mutatedCommandId,
    mutatedChords: options.mutatedChords,
    deferRepairs: options.deferRepairs ?? options.layoutMap.status === 'loading',
    activeBefore: previousKeymap ? (commandId, chord) => previousKeymap[commandId].includes(chord) : undefined,
    notices,
    removeDurably: (commandId, chord) => {
      durable[commandId] = durable[commandId].filter((item) => item !== chord);
      durableChanges.add(commandId);
    },
  });

  const acceptedKeys = new Set(accepted.map(({ commandId, chord }) => `${commandId}\0${chord}`));
  const keymap = Object.fromEntries(keybindingCommandIds.map((id) => [id, working[id].filter((chord) => acceptedKeys.has(`${id}\0${chord}`))])) as unknown as Record<KeybindingCommandId, readonly string[]>;
  // N1/O1: a row that used its defaults and is written as a loser leaves out
  // every chord that lost to another command's saved override.
  const overridesPatch = Object.fromEntries([...durableChanges].map((id) => {
    const lost = Array.isArray(options.overrides[id]) ? new Set<string>() : chordsLostToSavedOverrides(notices, id, options.overrides);
    return [id, durable[id].filter((chord) => !lost.has(chord))];
  }));
  for (const id of keybindingCommandIds) notices.push(...findWarnings(id, keymap[id], options.platform));
  return { keymap, overridesPatch, notices };
}
